import type { SkuReadinessRequest, SkuReadinessResponse } from '@nadoutong/contracts';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Bulk SKU readiness bits (epic #1327 PR-8) over the PR-7 gateway
 * endpoint. The marketplace list renders one readiness chip per row, so
 * this is deliberately a single batched POST rather than the per-SKU
 * fan-out `useRoutingDefaultSkuTargetsMap` uses — the whole catalog is
 * one request.
 *
 * The gateway owns three bits (binding / probe / cost); pricing and
 * visibility are platform-owned and merged client-side from the catalog
 * the page already loads.
 */

const READINESS_PATH = '/v1/ops/tenant-routing/sku-readiness';

/** Mirrors the gateway's `MAX_SKU_READINESS_IDS` batch cap. */
export const MAX_SKU_READINESS_IDS = 500;

export const SKU_READINESS_QUERY_KEY = ['sku-readiness'] as const;

export async function fetchSkuReadiness(
  body: SkuReadinessRequest,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<SkuReadinessResponse> {
  return opsApiRequest<SkuReadinessResponse>(
    READINESS_PATH,
    { method: 'POST', body, signal },
    fetchImpl,
  );
}

export interface SkuReadinessBatch {
  /** Gateway bits keyed by SKU id; absent until the batch resolves. */
  readonly bitsBySku: ReadonlyMap<string, { readonly binding: boolean; readonly probe: boolean; readonly cost: boolean }>;
  readonly isPending: boolean;
  readonly isError: boolean;
  /** True when the caller passed more ids than the endpoint accepts. */
  readonly truncated: boolean;
}

const EMPTY_BITS: ReadonlyMap<string, { binding: boolean; probe: boolean; cost: boolean }> = new Map();

/**
 * The query key carries the exact id list, so any change to the set —
 * a catalog that grew, a caller passing a narrower slice — reads afresh
 * instead of serving another set's bits. The batch cap is applied here;
 * the caller renders the truncation notice from the returned flag.
 */
export function useSkuReadiness(skuIds: readonly string[]): SkuReadinessBatch {
  const { fetchImpl } = useSession();
  const truncated = skuIds.length > MAX_SKU_READINESS_IDS;
  const batchIds = useMemo(
    () => (truncated ? skuIds.slice(0, MAX_SKU_READINESS_IDS) : skuIds),
    [skuIds, truncated],
  );
  const query = useQuery({
    queryKey: [...SKU_READINESS_QUERY_KEY, batchIds.join(',')],
    queryFn: ({ signal }) => fetchSkuReadiness({ sku_ids: batchIds }, fetchImpl, signal),
    enabled: batchIds.length > 0,
  });
  const bitsBySku = useMemo(
    () => (query.data === undefined
      ? EMPTY_BITS
      : new Map(query.data.readiness.map((bits) => [bits.sku_id, {
          binding: bits.binding,
          probe: bits.probe,
          cost: bits.cost,
        }]))),
    [query.data],
  );
  return {
    bitsBySku,
    isPending: batchIds.length > 0 && query.isPending,
    isError: query.isError,
    truncated,
  };
}
